import {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Navigate, useParams} from 'react-router-dom';
import {getAnalytics, logEvent} from 'firebase/analytics';

import Loading from '../components/Loading';
import {getChatV2ErrorMessage, getChatV2RunDebug} from '../services/chatV2';

const DebugChatV2Run = ({authenticated}) => {
    const {threadId, runId} = useParams();
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [debugPayload, setDebugPayload] = useState(null);
    const [copyMessage, setCopyMessage] = useState('');

    useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'debug_chatv2_run',
        });
    }, []);

    useEffect(() => {
        if (!authenticated || !threadId || !runId) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        setDebugPayload(null);

        getChatV2RunDebug(threadId, runId)
            .then((response) => {
                setDebugPayload(response);
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(
                    getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) ||
                    'Failed to load Chat V2 run debug.',
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }, [authenticated, threadId, runId]);

    const prettyJson = useMemo(() => {
        if (!debugPayload) {
            return '';
        }

        return JSON.stringify(debugPayload, null, 2);
    }, [debugPayload]);

    const onCopyClick = async () => {
        if (!prettyJson) {
            return;
        }

        try {
            await navigator.clipboard.writeText(prettyJson);
            setCopyMessage('Copied');
        } catch (err) {
            console.log(err);
            setCopyMessage('Copy failed');
        }

        window.setTimeout(() => {
            setCopyMessage('');
        }, 2000);
    };

    if (!authenticated) {
        return <Navigate to={'/signin'}></Navigate>;
    }

    return (
        <div className="container appScreen">
            <div style={{maxWidth: '1200px', margin: '0 auto', padding: '20px'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '6px'}}>
                    <div style={{fontSize: '28px', fontWeight: 600}}>
                        Chat V2 Run Debug
                    </div>
                    <button
                        onClick={onCopyClick}
                        disabled={!prettyJson}
                        style={{
                            border: '1px solid var(--app-separator)',
                            background: prettyJson ? 'var(--app-background)' : 'var(--app-surface)',
                            color: 'var(--app-text)',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: prettyJson ? 'pointer' : 'default',
                            flexShrink: 0,
                        }}
                    >
                        {copyMessage || 'Copy JSON'}
                    </button>
                </div>
                <div style={{fontSize: '14px', color: 'var(--app-text-secondary)', marginBottom: '20px'}}>
                    {threadId && runId ? `${threadId} / ${runId}` : 'Missing thread id or run id'}
                </div>

                {loading && (
                    <Loading overrideStyle={{textAlign: 'center', padding: '20px 0px'}} />
                )}

                {!!errorMessage && !loading && (
                    <div style={{color: 'var(--app-danger)', fontSize: '14px', marginBottom: '16px'}}>
                        {errorMessage}
                    </div>
                )}

                {!loading && !errorMessage && debugPayload && (
                    <pre
                        style={{
                            margin: 0,
                            padding: '16px',
                            backgroundColor: 'var(--app-surface)',
                            border: '1px solid var(--app-separator)',
                            borderRadius: '12px',
                            overflowX: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '13px',
                            lineHeight: 1.5,
                        }}
                    >
                        {prettyJson}
                    </pre>
                )}
            </div>
        </div>
    );
};

DebugChatV2Run.propTypes = {
    authenticated: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
});

export default connect(mapStateToProps)(DebugChatV2Run);
